import {
  ApiError,
  DEFAULT_PARCEL,
  formatMoney,
  type ParcelInput,
  queryKeys,
  type ShipmentView,
  type ShippingRate,
} from '@mevaser/shared'
import {
  Alert,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'

interface Target {
  id: string
  orderNumber: string
}

/**
 * Rate shopping and label purchase for one order (t0800): staff enter the
 * package, fetch live carrier rates, pick one, and buy — in that order,
 * because a rate id only resolves against the shipment it was quoted on.
 *
 * The parcel starts from `DEFAULT_PARCEL` but is always shown and always
 * editable — nothing in the catalogue carries a product weight yet, so this
 * is the one place it gets entered, every time.
 */
export function ShipmentPurchaseDialog({
  order,
  open,
  onClose,
}: {
  order: Target | null
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [weightLb, setWeightLb] = useState('1')
  const [weightOz, setWeightOz] = useState('0')
  const [lengthIn, setLengthIn] = useState(String(DEFAULT_PARCEL.lengthIn))
  const [widthIn, setWidthIn] = useState(String(DEFAULT_PARCEL.widthIn))
  const [heightIn, setHeightIn] = useState(String(DEFAULT_PARCEL.heightIn))
  const [rates, setRates] = useState<ShippingRate[] | null>(null)
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null)
  const [bought, setBought] = useState<ShipmentView | null>(null)

  const reset = () => {
    setWeightLb('1')
    setWeightOz('0')
    setLengthIn(String(DEFAULT_PARCEL.lengthIn))
    setWidthIn(String(DEFAULT_PARCEL.widthIn))
    setHeightIn(String(DEFAULT_PARCEL.heightIn))
    setRates(null)
    setSelectedRateId(null)
    setBought(null)
  }

  const currentParcel = (): ParcelInput => ({
    weightOz: (Number(weightLb) || 0) * 16 + (Number(weightOz) || 0),
    lengthIn: Number(lengthIn) || 0,
    widthIn: Number(widthIn) || 0,
    heightIn: Number(heightIn) || 0,
  })

  const getRates = useMutation({
    mutationFn: () =>
      api.post<{ rates: ShippingRate[] }>(`/orders/${order?.id}/shipments/rates`, {
        parcel: currentParcel(),
      }),
    onSuccess: (data) => {
      setRates(data.rates)
      setSelectedRateId(data.rates[0]?.rateId ?? null)
    },
    onError: (error: Error) =>
      toast.error('Could not get rates', error instanceof ApiError ? error.message : undefined),
  })

  const buyLabel = useMutation({
    mutationFn: () => {
      const rate = rates?.find((r) => r.rateId === selectedRateId)
      if (!rate) throw new Error('Choose a rate first')
      return api.post<ShipmentView>(`/orders/${order?.id}/shipments`, {
        shipmentId: rate.shipmentId,
        rateId: rate.rateId,
        parcel: currentParcel(),
      })
    },
    onSuccess: async (shipment) => {
      setBought(shipment)
      if (order) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(order.id) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.orders.shipments(order.id) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.orders.events(order.id) }),
        ])
      }
      toast.success('Label bought')
    },
    onError: (error: Error) =>
      toast.error('Could not buy the label', error instanceof ApiError ? error.message : undefined),
  })

  const close = () => {
    reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Buy a shipping label — {order?.orderNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {bought ? (
            <div className="space-y-3">
              <Alert tone="success">
                Bought {bought.carrier} {bought.serviceName} for{' '}
                {formatMoney(bought.rateAmountCents)}. Tracking {bought.trackingNumber}.
              </Alert>
              {bought.labelUrl ? (
                <Button asChild variant="primary">
                  <a href={bought.labelUrl} target="_blank" rel="noreferrer">
                    Open label to print
                  </a>
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              <div>
                <p className="mb-2 text-sm font-medium">Package</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Weight (lb)">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={weightLb}
                        onChange={(e) => setWeightLb(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Weight (oz)">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        max={15}
                        value={weightOz}
                        onChange={(e) => setWeightOz(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Length (in)">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={lengthIn}
                        onChange={(e) => setLengthIn(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Width (in)">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={widthIn}
                        onChange={(e) => setWidthIn(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Height (in)">
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={heightIn}
                        onChange={(e) => setHeightIn(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <Button
                  className="mt-3"
                  variant="secondary"
                  onClick={() => getRates.mutate()}
                  disabled={getRates.isPending}
                >
                  {getRates.isPending ? 'Getting rates…' : 'Get rates'}
                </Button>
              </div>

              {rates ? (
                <div>
                  <p className="mb-2 text-sm font-medium">Choose a rate</p>
                  {rates.length === 0 ? (
                    <Alert tone="warning">No carrier offered a rate for this package.</Alert>
                  ) : (
                    <ul className="space-y-1.5">
                      {rates.map((rate) => (
                        <li key={rate.rateId}>
                          <button
                            type="button"
                            onClick={() => setSelectedRateId(rate.rateId)}
                            className={cn(
                              'flex w-full items-center justify-between rounded-md border px-3 py-2 text-start text-sm',
                              rate.rateId === selectedRateId
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-surface-muted',
                            )}
                          >
                            <span>
                              <span className="font-medium">
                                {rate.carrier} {rate.serviceName}
                              </span>
                              {rate.deliveryDays !== null ? (
                                <span className="text-foreground-muted">
                                  {' '}
                                  · {rate.deliveryDays}-day estimate
                                </span>
                              ) : null}
                            </span>
                            <span className="tabular font-medium">
                              {formatMoney(rate.amountCents)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            {bought ? 'Done' : 'Cancel'}
          </Button>
          {!bought && rates && rates.length > 0 ? (
            <Button
              variant="primary"
              onClick={() => buyLabel.mutate()}
              disabled={!selectedRateId || buyLabel.isPending}
            >
              {buyLabel.isPending ? 'Buying…' : 'Buy label'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
